import ValidateModule from "./ValidateModule";

import type {SortOrder} from "@esWebAPI/es/model/Feature";
import type {Criteria} from "@esWebAPI/es/model/Criteria";
import type {Entitlement, GeoMap, GeoMapOverwrite, LayerViewOverwrite, TargetGeoMapType, TempGeoMap} from "@esWebAPI/es/model/GeoMap";
import type AjaxRequestBroker from "../AjaxRequestBroker";
import type { ApiOpts, IEventChannel } from "../model/Models";
import type CacheModule from "./CacheModule";

/**
 * @class es.module.[resource]
 * Object providing [resource].
 */
export default class GeoMapModule {

    private readonly eventChannel: IEventChannel;
    private readonly rqBroker: AjaxRequestBroker;
    private readonly resourceName: string;
    private readonly cache: CacheModule;

    constructor(eventChannel: IEventChannel, rqBroker: AjaxRequestBroker, resourceName: string, cache: CacheModule) {
        this.eventChannel = eventChannel;
        this.cache = cache;
        this.rqBroker = rqBroker;
        this.resourceName = resourceName;


        eventChannel.addListener("onDestroy", function(){
            eventChannel = null; // prevent memory leaks when the map widget is destroyed
        });
    }

    /**
     * @function es.module.[resource].get
     * Get the resource from the server
     * @return a jquery deferred object resolved when the request has completed
     */
    public get (id: number, opts?: ApiOpts): JQuery.Deferred<GeoMap>{
        if (!ValidateModule.checkId(id)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName) + "/" + id;
        return this.cache.doGet(this.rqBroker, this.resourceName, url, id, opts);
    }

    /**
     * @function es.module.[resource].get
     * Get the resource from the server
     * @param ids
     * @return a jquery deferred object resolved when the request has completed
     */
    public getByIds (ids: number[], opts?: ApiOpts): JQuery.Deferred<GeoMap[]>{
        if (!ValidateModule.checkIds(ids)) {
            return es.util.deferred.rejected();
        }

        const url = es.api.getURL(this.resourceName) + "/ids?ids=" + es.util.url.stringify(ids);
        return this.cache.doGet(this.rqBroker, this.resourceName, url, ids, opts);
    }

    /**
     * @function es.module.[resource].get
     * Get the resource from the server
     * @param criteria
     * @param sortOrder
     * @param offset
     * @param length
     * @return a jquery deferred object resolved when the request has completed
     */
    public list (criteria?: Criteria, sortOrder?: SortOrder, offset?: number, length?: number, opts?: ApiOpts): JQuery.Deferred<GeoMap[]>{
        //return this.rqBroker.doGet({url: es.api.getListQueryURL(resourceName, criteria, sortOrder, offset, length)});
        return this.cache.doGetList(this.rqBroker, this.resourceName, criteria, sortOrder, offset, length, opts);
    }

    /**
     * @function es.module.[resource].get
     * Get the resource from the server
     * @param criteria
     * @param sortOrder
     * @param offset
     * @param length
     * @return a jquery deferred object resolved when the request has completed
     */
    public listShared (criteria?: Criteria, sortOrder?: SortOrder, offset?:number, length?:number, opts?: ApiOpts): JQuery.Deferred<GeoMap[]>{
        //return this.rqBroker.doGet({url: es.api.getListQueryURL(resourceName, criteria, sortOrder, offset, length)});
        return this.cache.doGetList(this.rqBroker, this.resourceName + "/list-shared", criteria, sortOrder, offset, length, opts);
    }

    /**
     * @function es.module.[resource].get
     * Get the resource from the server
     * @param criteria
     * @param sortOrder
     * @param offset
     * @param length
     * @param usedAfter time in miliseconds to list only objects used after given timestamp, 0 means ignore
     *             the timestamp
     * @return a jquery deferred object resolved when the request has completed
     */
    public listOwned (criteria?: Criteria, sortOrder?: SortOrder, offset?: number, length?: number, usedAfter?: number, opts?: ApiOpts): JQuery.Deferred<GeoMap[]>{
        return this.cache.doGetList(this.rqBroker, this.resourceName + "/list-owned", criteria, sortOrder, offset, length, opts, usedAfter);
    }

    /**
     * @function es.module.[resource].listexplicitshared
     * List geo maps which are not owned by the current user and are explicitly shared with them
     * @param criteria
     * @param sortOrder
     * @param offset
     * @param length
     * @return a jquery deferred object resolved when the request has completed
     */
    public listExplicitShared (criteria?: Criteria, sortOrder?: SortOrder, offset?: number, length?: number, opts?: ApiOpts): JQuery.Deferred<GeoMap[]>{
        return this.cache.doGetList(this.rqBroker, this.resourceName + "/list_explicit_shared", criteria, sortOrder, offset, length, opts);
    }

    /**
     * @function es.module.[resource].count
     * Get a count of resources matching the given Criteria
     * @param criteria
     * @return long
     */
    public count (criteria?: Criteria, opts?: ApiOpts): JQuery.Deferred<number>{
        const url = es.api.getURL(this.resourceName) + "/count" + (criteria ? "?criteria=" + es.util.url.stringify(criteria) : "");
        return this.rqBroker.doGet({url: url, opts: opts});
    }

    /**
     * @function es.module.[resource].count
     * Get a count of resources matching the given Criteria
     * @param criteria
     * @return long
     */
    public countOwned (criteria?: Criteria, opts?: ApiOpts): JQuery.Deferred<number>{
        const url = es.api.getURL(this.resourceName) + "/count-owned" + (criteria ? "?criteria=" + es.util.url.stringify(criteria) : "");
        return this.rqBroker.doGet({url: url, opts: opts});
    }

    /**
     * @function es.module.[resource].count
     * Get a count of resources matching the given Criteria
     * @param criteria
     * @return long
     */
    public countShared (criteria?: Criteria, opts?: ApiOpts): JQuery.Deferred<number>{
        const url = es.api.getURL(this.resourceName) + "/count-shared" + (criteria ? "?criteria=" + es.util.url.stringify(criteria) : "");
        return this.rqBroker.doGet({url: url, opts: opts});
    }

    /**
     * @function es.module.[resource].countexplicitshared
     * Count geo maps which are not owned by the current user and are explicitly shared with them
     * Count criteria
     * @return a jquery deferred object resolved when the request has completed
     */
    public countExplicitShared (criteria?: Criteria, opts?: ApiOpts): JQuery.Deferred<number>{
        const url = es.api.getURL(this.resourceName) + "/count_explicit_shared" + (criteria ? "?criteria=" + es.util.url.stringify(criteria) : "");
        return this.rqBroker.doGet({url: url, opts: opts});
    }

    /**
     * @function es.module.[resource].post
     * POST a new resource to the server (note: id attrib must be -1)
     * @param resourceObj e.g. {"id":-1,"owner":"admin","title":"MY DEFAULT MAP part deux","viewPoint":{"x":0.0,"y":0.0,"resolution":39135.7584765625},"baseMapType":"roadmap","workspaceId":100,"lastModified":1385476013406,}
     * @param createWithTemp automatically create and return a tempMap for the map if it (defaults to true)
     * @return a jquery deferred object resolved when the request has completed
     */
    public create (resourceObj: GeoMap, createWithTemp: boolean, opts?: ApiOpts): JQuery.Deferred<GeoMap>{
        if (!ValidateModule.checkMandatoryArg(resourceObj)) {
            return es.util.deferred.rejected();
        }

        //es.log.info("geomap create", resourceObj);

        if(es.isDeveloper && (!resourceObj["workspaceId"] || resourceObj.workspaceId < 1) ){
            // we should be creating GeoMaps without a valid workspaceId
            es.log.error('we should be creating GeoMaps without a valid workspaceId');
        }

        return this.rqBroker.doPost({
            url: es.api.getURL(this.resourceName) + "?createWithTemp=" + (createWithTemp === false ? false : true),
            opts: opts,
            data: resourceObj
        }).done((rs: GeoMap) => {
            this.cache.setEntity(this.resourceName, rs);
            this.cache.clear("Workspace");
            this.cache.clear("Security", "ownership_geomap");
            if (this.eventChannel){
                this.eventChannel.fireEvent("onCreateGeoMap", rs);
            }
        });
    }

    /**
     * @function es.module.[resource].put
     * PUT update a resource (note: id attrib must be valid)
     * @param resourceObj e.g. {"id":100,"owner":"admin","title":"MY DEFAULT MAP part deux","viewPoint":{"x":0.0,"y":0.0,"resolution":39135.7584765625},"baseMapType":"roadmap","workspaceId":100,"lastModified":1385476013406}
     * @return a jquery deferred object resolved when the request has completed
     */
    public update (resourceObj: GeoMap, opts?: ApiOpts): JQuery.Deferred<GeoMap>{
        if (!ValidateModule.checkMandatoryArg(resourceObj)) {
            return es.util.deferred.rejected();
        }
        if (resourceObj == null) {
            throw new Error("GeoMap to be updated can't be null");
        }
        if(es.isDeveloper && resourceObj.$type == "GeoMap" && !resourceObj["workspaceId"]){
            // I don't think we should be creating normal GeoMaps without a workspaceId
            es.log.error('we should not be creating normal GeoMaps without a workspaceId');
        }

        return this.rqBroker.doPut({
            url: es.api.getURL(this.resourceName) + "/" + resourceObj.id,
            data: resourceObj,
            opts: opts
        }).done((rs: GeoMap) => {
            this.cache.setEntity(this.resourceName, rs);
            this.cache.clear("Workspace");
            if (this.eventChannel) {
                this.eventChannel.fireEvent("onUpdateGeoMap", rs);
            }
        });
    }

    /**
     * @function es.module.[resource].del
     * Get the resource from the server
     * @param id of geoMap to remove
     * @param isRemoveSpecifiedMapOnly true if temp map is to be removed only. Default behaviour to remove both temp and corresponding master i.e. val is false if not specified.
     * @return a jquery deferred object resolved when the request has completed
     */
    public remove (id: number, isRemoveSpecifiedMapOnly?: boolean, opts?: ApiOpts): JQuery.Deferred<GeoMap>{
        if (!ValidateModule.checkId(id)) {
            return es.util.deferred.rejected();
        }

        return this.rqBroker.doDelete( {url: es.api.getURL(this.resourceName) + "/" + id + "?isRemoveSpecifiedMapOnly=" + (isRemoveSpecifiedMapOnly == true), opts: opts} ).done(
            (_rs: GeoMap) => {
                this.cache.clear(this.resourceName);
                this.cache.clear("LayerView_GeoMap");
                this.cache.clear("Workspace");
                this.cache.clear("geomap_template");
                this.cache.clear("Security", "ownership_geomap");
                if (this.eventChannel) {
                    this.eventChannel.fireEvent("onRemoveGeoMap", id);
                }
            }
        );
    }

    /**
     * Remove geomaps by ids.
     *
     * @param ids
     * @param opts
     * @returns list of successfully removed geomaps
     */
    public removeBulk (ids: number[], opts?: ApiOpts) : JQuery.Deferred<{removed: number[]}> {
        if (!ValidateModule.checkMandatoryArg(ids)) {
            return es.util.deferred.rejected();
        }

        return this.rqBroker.doPost({
            url: es.api.getURL(this.resourceName) + "/remove_bulk",
            opts: opts,
            data: ids
        }).done((rs: {removed: number[]}) => {

            this.cache.clear("LayerView_GeoMap");
            this.cache.clear("Workspace");
            this.cache.clear("geomap_template");
            this.cache.clear("Security", "ownership_geomap");

            for (let index=0; index< rs.removed.length; index++) {
                if (this.eventChannel) {
                    const id = rs.removed[index];
                    this.cache.clear(this.resourceName, id);
                    this.eventChannel.fireEvent("onRemoveGeoMap", id);
                }
            }
        });
    }

    /**
     * save/push/publishWorkingCopy a TempGeomap to its master map.
     *
     *      es.api.geoMap.publishWorkingCopy(tempGeoMapId, parentLastModified)
     *
     * TODO FIXME Still firing "onCloneGeoMap" event, as before this was calling clone... Should create new event and review listeners
     */
    public publishWorkingCopy (tempGeoMapId: number, parentLastModified: number|null, opts?: ApiOpts): JQuery.Deferred<GeoMap> {
        es.log.info("geoMap#publishWorkingCopy");

        // parentLastModified is optional, so if there is an object there (and no opts), it is opts...
        if (!opts && typeof parentLastModified == 'object') {
            opts = parentLastModified;
            parentLastModified = null;
        }

        if(!ValidateModule.checkId(tempGeoMapId)) {
            return es.util.deferred.rejected();
        }

        let url = es.api.getURL(this.resourceName) + "/publishWorkingCopy/" + tempGeoMapId;
        if (parentLastModified) {
            url += "?copyToLastModified=" + parentLastModified;
        }
        return this.rqBroker.doPut({"url": url, "opts": opts}).done((masterGeoMap: GeoMap) => {
            //this.cache.clearAll();
            this.cache.clear(this.resourceName);
            this.cache.clear("Workspace");
            this.cache.clear("LayerView");
            this.cache.clear("Security");
            this.cache.clear("Territory_loadAll");
            this.cache.clear("LayerInfo_LayerView");

            if (this.eventChannel) {
                this.eventChannel.fireEvent("onUpdateGeoMap", masterGeoMap);
                this.eventChannel.fireEvent("onCloneGeoMap", tempGeoMapId, masterGeoMap.id, masterGeoMap);
            }
        });
    }

    /**
     * revert/pull/resetWorkingCopy a TempGeomap from its master map.
     *      es.api.geoMap.publishWorkingCopy(tempGeoMapId, parentLastModified)
     * TODO: this is only used in fris/api tests
     */
    public resetWorkingCopy (tempGeoMapId: number, parentLastModified: number, opts?: ApiOpts): JQuery.Deferred<TempGeoMap> {
        es.log.info("geoMap#resetWorkingCopy");

        if(!ValidateModule.checkId(tempGeoMapId)) {
            return es.util.deferred.rejected();
        }

        let url = es.api.getURL(this.resourceName) + "/resetWorkingCopy/" + tempGeoMapId;
        if (parentLastModified) {
            url += "?copyToLastModified=" + parentLastModified;
        }

        opts = $.extend(opts, {"resolveWithXHR": true});

        return this.rqBroker.doPut({"url": url, "opts": opts}).done((tempGeoMap: TempGeoMap, jqXHR: JQuery.jqXHR) =>{
            //this.cache.clearAll();
            this.cache.clear(this.resourceName);
            this.cache.clear("Workspace");
            this.cache.clear("LayerView");
            this.cache.clear("Security");
            this.cache.clear("Territory");
            if (this.eventChannel) {
                const masterGeoMapId = parseInt(jqXHR.getResponseHeader("copyToId") as string);
                this.eventChannel.fireEvent("onUpdateGeoMap", tempGeoMap);
                //this.eventChannel.fireEvent("onCloneGeoMap", masterGeoMapId, tempGeoMap.id, tempGeoMapId);
            }
        });
    }

    /**
     * Clones a temp map into a new master map.
     * (effectively changes existing save-as behaviour to create-a-copy behaviour)
     * @param tempGeoMapId
     * @param title
     * @param opts
     */
    public cloneTempToNewMaster(tempGeoMapId: number, title?: string, opts?: ApiOpts): JQuery.Deferred<GeoMap> {

        if(!ValidateModule.checkId(tempGeoMapId)) {
            return es.util.deferred.rejected();
        }

        const data = {
            'geoMapId': tempGeoMapId,
            'title': title,
            'geoMapOverwrite': {
                viewPointOverwrite: null
            }
        };

        let url = es.api.getURL(this.resourceName) + "/clone_temp_to_new_master/" + tempGeoMapId;

        return this.rqBroker.doPut({
            url: url,
            opts: opts,
            data: data
        }).done((masterGeoMap: GeoMap) => {
            //this.cache.clearAll();
            this.cache.clear(this.resourceName);
            this.cache.clear("Workspace");
            this.cache.clear("LayerView");
            this.cache.clear("Security");
            this.cache.clear("Territory_loadAll");
            this.cache.clear("LayerInfo_LayerView");

            if (this.eventChannel) {
                this.eventChannel.fireEvent("onUpdateGeoMap", masterGeoMap);
            }
        });
    }

    /**
     * @function cloneToWorkspace
     * PUT update a resource on the server
     * @param geoMapId geoMap to copy
     * @param title title of the new workspace/geoMap
     * @param geoMapOverwrite geoMap properties to overwrite
     * @param layerViewsOverwrites layerview properties to overwrite
     * @param geoMapType "TerritoryGeoMap" | "ScheduleGeoMap" | "GeoMap"
     * @param opts ApiOpts
     * @return a jquery deferred object resolved when the request has completed
     */
    public cloneToWorkspace (
        geoMapId: number,
        title: string,
        geoMapType: TargetGeoMapType,
        geoMapOverwrite?: GeoMapOverwrite,
        layerViewsOverwrites?: LayerViewOverwrite[],
        opts?: ApiOpts
    ): JQuery.Deferred<GeoMap> {

        const url = es.api.getURL(this.resourceName) + "/cloneToWorkspace/" + geoMapId;
        const data = {
            geoMapId: geoMapId,
            title: title,
            geoMapOverwrite: geoMapOverwrite,
            layerViewsOverwrites: layerViewsOverwrites,
            targetGeoMapType: geoMapType
        };

        return this.rqBroker.doPut({
            url: url,
            opts: opts,
            data: data
        }).done((rs: GeoMap) => {
            this.cache.setEntity(this.resourceName, rs);
            this.cache.clear("Workspace");
            this.cache.clear("Security", "ownership_workspace");
            this.cache.clear("Security", "ownership_geomap");
            this.cache.clear("Security", "ownership_layerview");
        });
    }

    /**
     * @function cloneAndConvertToSchedule
     * Create a ScheduleGeoMap from a basic GeoMap
     * @param geoMapId geoMap to copy
     * @param title title of the new workspace/geoMap
     * @param geoMapOverwrite geoMap properties to overwrite
     * @param layerViewsOverwrites layerview properties to overwrite
     * @return a jquery deferred object resolved when the request has completed
     */
    public cloneAndConvertToSchedule (geoMapId: number, title: string, geoMapOverwrite: GeoMapOverwrite, layerViewsOverwrites: LayerViewOverwrite[], opts?: ApiOpts): JQuery.Deferred<GeoMap>{
        const url = es.api.getURL(this.resourceName) + "/cloneAndConvertGeoMap/" + geoMapId;
        const data = {
            geoMapId: geoMapId,
            title: title,
            geoMapOverwrite: geoMapOverwrite,
            layerViewsOverwrites: layerViewsOverwrites,
            targetGeoMapType: "SCHEDULE_GEO_MAP"
        };

        return this.rqBroker.doPut({
            url: url,
            opts: opts,
            data: data
        }).done((rs: GeoMap) => {
            this.cache.setEntity(this.resourceName, rs);
            this.cache.clear("Workspace");
            this.cache.clear("Security", "ownership_workspace");
            this.cache.clear("Security", "ownership_geomap");
            this.cache.clear("Security", "ownership_layerview");
        });
    }

    /**
     * Shares the geomap, shares the workspace as necessary, sends email notifications.
     */
    public shareGeoMap (id: number, geoMapEntitlements: Entitlement[], emailNotify: boolean, opts?: ApiOpts): JQuery.Deferred<{
        id: number
        owner: string
        emailAddress: string
        firstName: string
        lastName: string
        phoneNumber: string
    }> {

        const url = es.api.getURL("geomap_share/") + id + "?emailNotify=" + emailNotify;

        return this.rqBroker.doPost({
            'url':url,
            'data': geoMapEntitlements,
            opts: opts
        }).done(() => {
            this.cache.clear('entitlements_geomap', id);
            this.cache.clear('entitlements_layerview');
            this.cache.clear("Security"); // required?
            this.eventChannel.fireEvent("onUpdateEntitlements", "geomap", id); // TODO FIXME should fake all entitlements update?
        });
    }

    /**
     * Get geomaps per ownership and type.
     * If there is a working copy, it retrieves the TempGeoMap instead of the master GeoMap
     *
     * @param listOwnership one of: 'OWNED', 'SHARED', 'OWNED_AND_SHARED'
     * @param listGeoMapType one of: 'REGULAR', 'REGULAR_ROUTE_ONLY', 'SCHEDULE', 'TERRITORY' i.e. regular maps (with or without routes), regular maps with routes, schedule maps, territory maps
     * @param opts
     */
    public getWorkingCopyGeoMapList (listOwnership: "OWNED" | "SHARED" | "OWNED_AND_SHARED",
                              listGeoMapType: "REGULAR" | "REGULAR_ROUTE_ONLY" | "SCHEDULE" | "TERRITORY",
                              opts?: ApiOpts): JQuery.Deferred<{[key: string] : GeoMap[]}> {
        if (!ValidateModule.checkMandatoryArg(listOwnership, 'OWNED', 'SHARED', 'OWNED_AND_SHARED')) {
            return es.util.deferred.rejected();
        }

        if (!ValidateModule.checkMandatoryArg(listGeoMapType, 'REGULAR', 'REGULAR_ROUTE_ONLY', 'SCHEDULE', 'TERRITORY')) {
            return es.util.deferred.rejected();
        }

        return this.rqBroker.doGet({url: es.api.getURL(this.resourceName) + "/working_copy/" + listOwnership + '/' + listGeoMapType, opts: opts});
    }

    public moveToWorkspace (geoMapId: number, workspaceId: number, opts?: ApiOpts): JQuery.Deferred<GeoMap> {
        const url = es.api.getURL(this.resourceName) + "/moveToWorkspace/" + geoMapId + "?workspaceId=" + workspaceId;

        return this.rqBroker.doPut({
            url: url,
            opts: opts,
            data: {}
        }).done((rs: GeoMap) => {
            this.cache.setEntity(this.resourceName, rs);
            this.cache.clear("Workspace");
            if (this.eventChannel) {
                this.eventChannel.fireEvent("onUpdateGeoMap", rs);
            }
        });
    }
}